import * as Popover from "@radix-ui/react-popover";
import moment from "moment";
import {
  CSSProperties,
  forwardRef,
  PointerEvent,
  ReactNode,
  useContext,
  useState,
} from "react";
import { GutterBooking, GutterBookingContext } from "./context";
import { formatTimeRange } from "util/time";
import "./index.css";

/** The parts of react-big-calendar's per-column slot metrics we use. */
type SlotMetrics = {
  closestSlotFromPoint: (
    point: { x: number; y: number },
    boundaryRect: DOMRect
  ) => Date;
  getRange: (start: Date, end: Date) => { top: number; height: number };
};

/** The calendar's step: each slot is 15 minutes. */
const STEP_MINUTES = 15;

/** What a click books. */
const CLICK_MINUTES = 30;

/** How far the pointer moves, up or down, before a press is a drag. */
const DRAG_PIXELS = 4;

type Drag = {
  anchor: Date;
  current: Date;
  /** Where the press started, to tell a drag from a click. */
  startY: number;
  moved: boolean;
};

/**
 * A click books 30 minutes from its slot. A drag books the slots it covers,
 * so a drag within one slot books 15 minutes. Neither goes past midnight.
 */
function rangeOf({ anchor, current, moved }: Drag): GutterBooking {
  const [first, last] =
    +anchor < +current ? [anchor, current] : [current, anchor];
  const start = moved ? first : anchor;
  const end = moved
    ? moment(last).add(STEP_MINUTES, "minutes")
    : moment(anchor).add(CLICK_MINUTES, "minutes");
  const nextMidnight = moment(start).add(1, "day").startOf("day");
  return { start, end: moment.min(end, nextMidnight).toDate() };
}

/** Presses here belong to react-big-calendar: a meeting, its handle, "+N more". */
const LIBRARY_TARGETS =
  ".rbc-event, .rbc-addons-dnd-resize-ns-anchor, .rbc-show-more";

/**
 * react-big-calendar's day column wrapper. Pressing on any empty space in the
 * column, or in the 10px strip on its right that meetings never cover (so a
 * packed column still works), selects a time; letting go opens the booking
 * pop-up, anchored to the selection. Pressing on a meeting is left to the
 * drag-and-drop add-on, which moves it as before.
 *
 * It renders the column exactly like the library's default wrapper, library
 * children first. Module scope on purpose: a new component type would remount
 * every column.
 */
const GutterDayColumn = forwardRef<
  HTMLDivElement,
  {
    children?: ReactNode;
    className?: string;
    style?: CSSProperties;
    date: Date;
    slotMetrics: SlotMetrics;
  }
>(function GutterDayColumn(
  { children, className, style, date, slotMetrics },
  ref
) {
  const gutter = useContext(GutterBookingContext);
  const [drag, setDrag] = useState<Drag | null>(null);

  const slotAt = (e: PointerEvent<HTMLDivElement>) =>
    slotMetrics.closestSlotFromPoint(
      { x: e.clientX, y: e.clientY },
      e.currentTarget.getBoundingClientRect()
    );

  const booking = gutter?.booking;
  const shown =
    (drag && rangeOf(drag)) ||
    (booking && moment(booking.start).isSame(date, "day") ? booking : null);

  const position = shown && slotMetrics.getRange(shown.start, shown.end);
  // The open pop-up's anchor: where it opened, which may no longer be the
  // time.
  const anchor = booking && gutter?.anchor;
  const anchored =
    !drag && anchor && moment(anchor.start).isSame(date, "day")
      ? slotMetrics.getRange(anchor.start, anchor.end)
      : null;

  return (
    <div
      ref={ref}
      className={className}
      style={style}
      onPointerDown={(e) => {
        if (!gutter || e.button !== 0) return;
        // A meeting's dialog, a pop-up or a menu opened from this column
        // sits in a portal, and React bubbles its presses here; they aren't
        // the column's. Capturing them would steal their clicks.
        if (!e.currentTarget.contains(e.target as Node)) return;
        if ((e.target as Element).closest(LIBRARY_TARGETS)) return;
        // No text selection and no emulated mouse events.
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        const slot = slotAt(e);
        setDrag({
          anchor: slot,
          current: slot,
          startY: e.clientY,
          moved: false,
        });
      }}
      onPointerMove={(e) => {
        if (!drag) return;
        const slot = slotAt(e);
        const moved =
          drag.moved || Math.abs(e.clientY - drag.startY) >= DRAG_PIXELS;
        if (+slot !== +drag.current || moved !== drag.moved) {
          setDrag({ ...drag, current: slot, moved });
        }
      }}
      onPointerUp={() => {
        if (!drag || !gutter) return;
        gutter.openBooking(rangeOf(drag));
        setDrag(null);
      }}
      onPointerCancel={() => setDrag(null)}
    >
      {children}
      {gutter && <div className="tp-gutter" title="Click or drag to book" />}
      {shown && position && (
        <div
          className="tp-gutter-ghost"
          style={{ top: `${position.top}%`, height: `${position.height}%` }}
        >
          {formatTimeRange(shown.start, shown.end)}
        </div>
      )}
      {anchored && (
        <Popover.Anchor asChild>
          <div
            className="tp-gutter-anchor"
            style={{ top: `${anchored.top}%`, height: `${anchored.height}%` }}
          />
        </Popover.Anchor>
      )}
    </div>
  );
});

export default GutterDayColumn;
